import Vue from 'vue'
import { Module, ActionContext } from 'vuex'
import { RootState, CalendarState } from './state_models'
import Calendar from '../models/calendar'
import sortBy from "lodash-es/sortBy"
import CalendarsServices from '@/services/CalendarService'

export const calendars: Module<CalendarState, RootState> = {
  namespaced: true,
  state: { calendars: [], locationId: undefined, subAccountConfig: undefined, services:[], servicesLocationId: undefined },
  getters: {
    getById: (state: CalendarState) => {
      return (id: string) => state.calendars.find(x => x.id === id)
    },
    getSubAccountConfig(state: CalendarState) {
      return state.subAccountConfig
    },
    isServicesEnabled(state: CalendarState) {
      return state.subAccountConfig?.modules?.includes('services')
    },
    isServicesMigrated(state: CalendarState) {
      return state.subAccountConfig?.isServicesMigrated
    },
    getAll: (state: CalendarState) => {
      return state.calendars
    },
    getAllServices: (state: CalendarState) => {
      return state.services || []
    }
  },
  mutations: {
    set: (
      state: CalendarState,
      payload: { calendars: any[]; locationId: string; subAccountConfig: any ,services:any[],servicesLocationId:string}
    ) => {
      if (payload.calendars) {
        state.calendars = payload.calendars
      }
      if (payload.subAccountConfig) {
        state.subAccountConfig = payload.subAccountConfig || {}
      }
      if (payload.locationId) {
        state.locationId = payload.locationId
      }
      if (payload.services) {
        state.services = payload.services
      }
      if (payload.servicesLocationId) {
        state.servicesLocationId = payload.servicesLocationId
      }
    },
    clearAll(state: CalendarState) {
      state.calendars = []
      state.locationId = undefined
      state.services = []
      state.subAccountConfig = undefined
      state.servicesLocationId = undefined
    }
  },
  actions: {
    async syncAll(
      context: ActionContext<CalendarState, RootState>,
      options: string | {locationId:string,force:boolean},
    ) {
      let force = false
      let locationId = options
      if(typeof options === 'object')
      {
        locationId = options.locationId
        force = options.force
      }
      if (!locationId) {
        context.commit('clearAll')
        return
      }

      if (context.state.locationId === locationId && !force) {
        return context.state.calendars
      }

      return new Promise((resolve, reject) => {
        Calendar.fetchAllByLocation(locationId as string)
          .get()
          .then(snapshot => {
            const calendars = snapshot.docs
              .map(d => {
                return {
                  id: d.id,
                  ...d.data()
                }
              })
            const sortedCalendars = sortBy(calendars, (c) => c.name ? c.name.toUpperCase() : '')
            context.commit('set', {
              locationId: locationId,
              calendars: sortedCalendars
            })

            resolve(context.state.calendars)
          })
          .catch(err => {
            console.error(err)
            resolve(err)
          })
      })
    },
    logout: {
      root: true,
      handler: (context: ActionContext<CalendarState, RootState>) => {
        context.commit('clearAll')
      }
    },
    clearAll: {
      handler: (context: ActionContext<CalendarState, RootState>) => {
        context.commit('clearAll')
      }
    },
    async syncSubAccountConfig(context: ActionContext<CalendarState, RootState>, locationId: string) {
      if (!locationId) {
        return
      }
      try {
        const response = await CalendarsServices.getSubAccountConfig(locationId)
        const config = response
        context.commit('set', {
          subAccountConfig: {
            modules: (config?.subAccountConfig?.modules?.length > 0) 
              ? config.subAccountConfig.modules 
              : ['meetings', 'services'],
            isServicesMigrated: config?.isMigrationCompleted
          }
        });
      } catch (err) {
        context.commit('set', {
          subAccountConfig: {
            modules: ['meetings'],
            isServicesMigrated: false
          }
        });
        return
      }
    },
    async syncCalendarServices(context: ActionContext<CalendarState, RootState>, locationId: string) {      
      if (!locationId) {
        return
      }

      if (context.state.servicesLocationId === locationId) {
        return context.state.services
      }

      try {
        const response = await CalendarsServices.getAllServices(locationId)
        context.commit('set', {
          services: response?.services || []
        })
      } catch (err) {
        context.commit('set', {
          services: []
        })
        console.error(err)
      }
    }
  }
}
